/**
 * csvExport — minimal, dependency-free CSV download helper.
 *
 * Purpose: let CFO-grade surfaces (pay runs, variance, accounting)
 * dump the rows they already render into a spreadsheet without
 * round-tripping through a server.
 *
 * Scope:
 *   • Pure presentation. No business logic, no resolver bypass.
 *   • Caller passes already-resolved rows; we only format + download.
 *   • Excel-friendly: CRLF line endings + UTF-8 BOM so accents and
 *     "$" symbols open cleanly in Excel/Numbers/Sheets.
 */

/** RFC 4180 cell escaping. */
function escapeCell(v: unknown): string {
  if (v == null) return "";
  const s = typeof v === "string" ? v : String(v);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/** Build a CSV string from headers + rows. */
export function toCsv(headers: string[], rows: Array<Array<unknown>>): string {
  const lines = [headers.map(escapeCell).join(",")];
  for (const row of rows) lines.push(row.map(escapeCell).join(","));
  return lines.join("\r\n");
}

/** Trigger a browser download of the CSV. Returns the filename used. */
export function downloadCsv(filename: string, csv: string): string {
  const bom = "\uFEFF"; // Excel-friendly UTF-8 hint
  const blob = new Blob([bom + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so Safari has a chance to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return filename;
}

/** Convenience: build + download in one call. */
export function exportCsv(
  filename: string,
  headers: string[],
  rows: Array<Array<unknown>>,
): string {
  return downloadCsv(filename, toCsv(headers, rows));
}

/** Stamp a filename with an ISO date so successive exports don't collide. */
export function stampFilename(base: string, ext = "csv"): string {
  const d = new Date();
  const iso = d.toISOString().slice(0, 10);
  return `${base}_${iso}.${ext}`;
}
